import { Cancel } from '@mui/icons-material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  Divider,
  IconButton,
  Modal,
  Portal,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import React from 'react';
import {
  useMutation,
  useQuery
} from 'react-fetching-library';
import { useForm } from 'react-hook-form';
import { Document, Page } from 'react-pdf';
import FormProvider from '../../../components/hook-form/FormProvider';
import {
  getDocumentToSignAction,
  getInstanceSignAgreementData,
  signDocumentAction
} from '../../../services/request';
import { useParams } from 'react-router-dom';
import { LoadingButton } from '@mui/lab';
import { MobilePDFReader } from 'react-read-pdf';
import { useResponsive } from '../../../services/hooks';
import { formatDateTime } from '../../../services/functions';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: {
    xs: '90%',
    md: 400
  },
  maxHeight: '100vh'
};

function SignModal({ id, openModal, handleClose, setIsSigned }) {
  const { annex_nr, model } = useParams();
  const { loading, payload, error } = useQuery(
    getInstanceSignAgreementData(id, model)
  );
  const {
    loading: mloading,
    mutate,
    error: merror
  } = useMutation(signDocumentAction);
  const [employeeData, setEmployeeData] = React.useState({});

  React.useEffect(() => {
    if (!error && !loading) {
      setEmployeeData(payload);
    }
  }, [loading]);

  const methods = useForm({
    defaultValues: {
      full_name: '',
      annex_nr: annex_nr ? annex_nr : 0,
      model
    },
    mode: 'onSubmit'
  });

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = methods;

  const onSubmit = handleSubmit(async (data, e) => {
    e?.preventDefault();
    const { merror, payload: mpayload } = await mutate(data, id);
    if (!merror) {
      setIsSigned(true);
    } else {
      console.log('Document NOT SIGNED');
    }
  });


  const validateFullName = value => {
    // Normalizuj wartości do małych liter przed porównaniem
    const normalizedValue = value.trim().toLowerCase();
    const normalizedEmployeeName = employeeData.full_name.trim().toLowerCase();

    if (normalizedValue !== normalizedEmployeeName) {
      return 'Wprowadź poprawne imię i nazwisko, zgodne z podanymi wcześniej danymi';
    }

    return true;
  };

  return (
    <Portal>
      <Modal open={openModal} onClose={handleClose}>
        <Card sx={style} className="overflow-auto">
          <CardContent
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexDirection: 'column',
              p: 7,
              position: 'relative'
            }}
          >
            <IconButton
              disabled={mloading}
              color="primary"
              onClick={handleClose}
              sx={{
                position: 'absolute',
                top: 0,
                right: 0
              }}
            >
              <Cancel fontSize="large" />
            </IconButton>
            <FormProvider
              methods={methods}
              onSubmit={onSubmit}
              className="flex flex-col gap-4"
            >
              <TextField
                label="Imię i nazwisko"
                sx={{ width: '100%', mb: 2 }}
                fullWidth
                {...register('full_name', {
                  required: 'To pole jest wymagane',
                  validate: validateFullName
                })}
                error={errors.full_name ? true : false}
                helperText={errors.full_name?.message}
              />
              <LoadingButton sx={{ textTransform: 'none' }} type="submit" variant="contained" disabled={mloading} loading={mloading} loadingPosition="start">
                {mloading ? <Typography variant="button">TWOJE DOKUMENTY SĄ WŁAŚNIE PODPISYWANE</Typography> : `Akceptuję ofertę zawarcia ${Number(annex_nr) ? 'aneksu' : 'umowy wraz z otrzymanymi załącznikami'}`}
              </LoadingButton>
            </FormProvider>
          </CardContent>
        </Card>
      </Modal>
    </Portal>
  );
}

export default function DocumentSign({ id, setIsSigned, signingDueDate }) {
  const { annex_nr, model } = useParams();
  const [showDocument, setShowDocument] = React.useState(true);
  const [numPages, setNumPages] = React.useState(0);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [agreementURL, setAgreementURL] = React.useState('');
  const [timeLeft, setTimeLeft] = React.useState((Number(annex_nr) ? 2 : 5) * 60 * 1000);
  // const [timeLeft, setTimeLeft] = React.useState(10 * 1000);
  const [openModal, setOpenModal] = React.useState(false);
  const isDesktop = useResponsive('up', 'lg');
  const [heightAdapted, setHeightAdapted] = React.useState(false);

  const template_name = Number(annex_nr) ? 'annex' : 'agreement';
  const fileName = 'Umowa';

  const { loading, payload, error } = useQuery(
    getDocumentToSignAction(id, template_name, fileName, model, annex_nr)
  );

  React.useEffect(() => {
    if (!error && !loading) {
      setAgreementURL(window.URL.createObjectURL(payload));
    }
  }, [loading]);

  React.useEffect(() => {
    const intervalId = setInterval(() => {
      // setTimeLeft(prevTimeLeft => prevTimeLeft > 0 ? prevTimeLeft - 10 * 1000 : 0);
      setTimeLeft(prevTimeLeft => prevTimeLeft > 0 ? prevTimeLeft - 60 * 1000 : 0);
    }, 60 * 1000);
    // }, 10 * 1000);

    return () => clearInterval(intervalId);
  }, [timeLeft]);

  const handleClose = () => {
    setOpenModal(false);
  };

  const handlePDFLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  React.useEffect(() => {
    if (isDesktop) {
      return;
    }
    const handlePageChange = evt => {
      setCurrentPage(evt.pageNumber);
    };
    document.addEventListener('pagechange', handlePageChange);
  }, []);

  React.useEffect(() => {
    const pages = document.querySelectorAll('.page');
    if (pages.length == 0) {
      return;
    }
    const maxPageElement = Array.from(pages).reduce((maxElement, currentElement) => {
      const maxPageNumber = parseInt(maxElement.getAttribute('data-page-number') || '0', 10);
      const currentPageNumber = parseInt(currentElement.getAttribute('data-page-number') || '0', 10);

      return currentPageNumber > maxPageNumber ? currentElement : maxElement;
    }, pages[0]);
    setNumPages(maxPageElement.getAttribute('data-page-number'));
  }, [document.querySelectorAll('.page').length]);

  React.useEffect(() => {
    setTimeout(() => {
      const pageElements = document.querySelectorAll('.page[data-page-number]');
      if (pageElements) {
        pageElements.forEach(pageElement => {
          pageElement.style.overflow = 'hidden';
          pageElement.style.margin = 'auto';
          if (!heightAdapted) {
            const intHeight = parseInt(pageElement.style.height, 10);
            const newHeight = intHeight + 3;
            const newPxHeight = `${newHeight}px`;
            pageElement.style.height = newPxHeight;
            setHeightAdapted(true);
          }
        });
      }
    }, 400);
  }, [document.querySelectorAll('.page[data-page-number]')]);

  return (
    <Box sx={{ overflow: 'hidden' }}
    >
      {showDocument && (
        isDesktop
          ? <Dialog
            open={showDocument}
            PaperProps={{
              sx: {
                maxWidth: 'none'
              }
            }}
          >
            <Stack flexDirection="row">
              {currentPage != 1 ? (
                <IconButton
                  onClick={() => setCurrentPage(currentPage - 1)}
                  color="primary"
                >
                  <ArrowBackIcon />
                </IconButton>
              ) : null}
              <Document
                file={agreementURL}
                onLoadSuccess={handlePDFLoadSuccess}
                sx={{ width: 100 }}
              >
                <Page pageNumber={currentPage} />
              </Document>
              {currentPage != numPages ? (
                <IconButton
                  onClick={() => setCurrentPage(currentPage + 1)}
                  color="primary"
                >
                  <ArrowForwardIcon />
                </IconButton>
              ) : null}
            </Stack>
            {currentPage == numPages && (
              <Button
                type="submit"
                sx={{ textTransform: 'none' }}
                onClick={() => setOpenModal(true)}
                disabled={timeLeft > 0}
                variant="contained"
              >
                {timeLeft > 0
                  ? `Podpisz za ${Math.ceil(timeLeft / 60000)} min`
                  : Number(annex_nr) ? 'Zaakceptuj aneks' : 'Zaakceptuj umowę'}
              </Button>
            )}
          </Dialog>
          : <div style={{ overflow: 'scroll !important', height: 700 }}>
            {agreementURL && <MobilePDFReader url={agreementURL} isShowFooter={false} isShowHeader={false} minScale={0.4} />}
            <div style={{ position: 'fixed', bottom: 0, width: '100%', textAlign: 'center', padding: '100px' }}>
              {numPages == currentPage && <Button
                type="submit"
                sx={{ textTransform: 'none' }}
                onClick={() => setOpenModal(true)}
                disabled={timeLeft > 0}
                variant="contained"
              >
                {timeLeft > 0
                  ? `Podpisz za ${Math.ceil(timeLeft / 60000)} min`
                  : Number(annex_nr) ? 'Zaakceptuj aneks' : 'Zaakceptuj umowę'}
              </Button>}
            </div>
          </div>
      )}
      <SignModal
        openModal={openModal}
        handleClose={handleClose}
        id={id}
        setIsSigned={setIsSigned}
      />
    </Box>
  );
}
